import { useId, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { SortableList } from "@/components/common/SortableList";
import { Button } from "@/components/design/Button";
import { userErrorMessage } from "@/lib/user-error";
import { ToplistCard, type ToplistItem } from "./ToplistCard";
import { ToplistSearch, type ToplistSearchSource } from "./ToplistSearch";

// Entries passed over shown before "Show all": two rows of four, four rows on a phone.
const PASSED_SHOWN = 8;

// The end of the toplist game: the picks as a list to put in order, each one
// to take off again, and below it what the rounds showed and was not picked,
// plus the search, to swap one in while a place is open. "Save" writes the list
// to the profile once every place is filled; nothing is written before. Once
// saved the list is fixed: genesis writes a finished session only once.
export function ToplistResult({
  items,
  keep,
  passed,
  search,
  onMove,
  onRemove,
  onAdd,
  onMoreRounds,
  onApply,
  onRestart,
}: {
  // The list, best first.
  items: readonly ToplistItem[];
  // Places in the ranking: "Save" needs exactly that many.
  keep: number;
  // What the rounds showed and was not picked.
  passed: readonly ToplistItem[];
  search: ToplistSearchSource;
  onMove: (from: number, to: number) => void;
  onRemove: (id: string) => void;
  onAdd: (id: string) => void;
  // Back to the rounds for fresh suggestions; only offered while a place is open.
  onMoreRounds: () => void;
  onApply: () => Promise<void>;
  onRestart: () => void;
}) {
  const intl = useIntl();
  const passedId = useId();
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [showAllPassed, setShowAllPassed] = useState(false);
  // The row picked up to be placed by a tap; the hint says where to tap next.
  const [placing, setPlacing] = useState<ToplistItem | null>(null);
  const placingItem = placing && items.some((item) => item.id === placing.id) ? placing : null;
  const missing = Math.max(0, keep - items.length);
  const full = missing === 0;
  const shownPassed = showAllPassed ? passed : passed.slice(0, PASSED_SHOWN);

  async function apply() {
    setPlacing(null);
    setApplying(true);
    setError(null);
    try {
      await onApply();
      setApplied(true);
    } catch (err) {
      setError(userErrorMessage(intl, err));
    } finally {
      setApplying(false);
    }
  }

  const entry = (item: ToplistItem, index: number) => (
    <span className="flex min-w-0 flex-1 items-baseline gap-3">
      <span className="w-5 shrink-0 text-smycles-meta tabular-nums text-ink-muted">{index + 1}</span>
      <span className="smycles-field-value min-w-0 font-medium text-ink">{item.title}</span>
      {item.subtitle ? <span className="smycles-field-meta text-ink-muted">{item.subtitle}</span> : null}
    </span>
  );

  const removableEntry = (item: ToplistItem, index: number) => (
    <span className="flex items-center gap-2">
      {entry(item, index)}
      <Button
        variant="icon"
        size="sm"
        onClick={() => onRemove(item.id)}
        aria-label={intl.formatMessage(
          { id: "grow.games.toplist.remove", defaultMessage: "Remove {title}" },
          { title: item.title },
        )}
      >
        ×
      </Button>
    </span>
  );

  return (
    <fieldset disabled={applying} className="min-w-0">
      <div className="w-full max-w-[32rem]">
        <h2 className="text-smycles-title font-medium">
          <FormattedMessage id="grow.games.toplist.result.title" defaultMessage="Your top {count}" values={{ count: keep }} />
        </h2>
        {items.length > 0 ? (
          <div className="box mt-4 bg-surface px-2 py-2">
            {applied || applying ? (
              <ol>
                {items.map((item, index) => (
                  <li key={item.id} className="flex px-2 py-2">
                    {entry(item, index)}
                  </li>
                ))}
              </ol>
            ) : (
              <SortableList
                items={items}
                getKey={(item) => item.id}
                getLabel={(item) => item.title}
                renderItem={removableEntry}
                onMove={onMove}
                onPickedChange={setPlacing}
              />
            )}
          </div>
        ) : null}
        {!applied && !applying && items.length > 1 ? (
          <p className="mt-2 smycles-field-meta text-ink-muted">
            {placingItem ? (
              <FormattedMessage
                id="grow.games.toplist.result.placeHint"
                defaultMessage="Tap the new place for {item}."
                values={{ item: placingItem.title }}
              />
            ) : (
              <FormattedMessage
                id="grow.games.toplist.result.hint"
                defaultMessage="Drag to reorder, or tap one and then tap its new place."
              />
            )}
          </p>
        ) : null}

        {applied ? (
          <div role="status" className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="smycles-field-value text-ink">
              <FormattedMessage id="grow.games.toplist.result.saved" defaultMessage="Saved to your profile." />
            </span>
            <Link
              to="/save/basics"
              className="inline-flex items-center gap-1 text-smycles-micro uppercase tracking-[0.2em] text-ink-muted transition hover:text-ink-accent"
            >
              <FormattedMessage id="grow.games.toplist.result.toProfile" defaultMessage="To profile" />{" "}
              <span aria-hidden="true">↗</span>
            </Link>
          </div>
        ) : null}
        {error ? <p className="mt-4 smycles-field-meta text-ink">{error}</p> : null}

        <div className="mt-6 flex flex-wrap gap-3">
          {applied ? null : (
            <Button onClick={() => void apply()} disabled={applying || !full}>
              <FormattedMessage id="grow.games.toplist.result.apply" defaultMessage="Save to profile" />
            </Button>
          )}
          <Button
            variant="secondary"
            disabled={applying}
            // Playing again throws away a ranking that was never saved; that needs a yes.
            onClick={() => (applied ? onRestart() : setConfirmDiscard(true))}
          >
            <FormattedMessage id="grow.games.toplist.result.again" defaultMessage="Play again" />
          </Button>
        </div>
        {!applied && !full ? (
          <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <p role="status" className="smycles-field-meta text-ink-muted">
              <FormattedMessage
                id="grow.games.toplist.result.missing"
                defaultMessage="{missing} more for your top {keep}"
                values={{ missing, keep }}
              />
            </p>
            <Button variant="ghost" size="sm" onClick={onMoreRounds}>
              <FormattedMessage id="grow.games.toplist.result.moreRounds" defaultMessage="More suggestions" />
            </Button>
          </div>
        ) : null}
      </div>

      {applied ? null : (
        <div className="mt-10">
          {full ? (
            <p className="mb-3 smycles-field-meta text-ink-muted">
              <FormattedMessage
                id="grow.games.toplist.result.full"
                defaultMessage="To add another, take one off your list first."
                values={{ genus: search.genus }}
              />
            </p>
          ) : null}
          <ToplistSearch source={search} picked={new Set(items.map((item) => item.id))} full={full} onChoose={onAdd}>
            {passed.length > 0 ? (
              <div className="mt-6">
                <p id={passedId} className="smycles-field-label">
                  <FormattedMessage id="grow.games.toplist.result.passed" defaultMessage="Not picked" />
                </p>
                <div role="group" aria-labelledby={passedId} className="mt-2 grid grid-cols-2 gap-3 @md:grid-cols-4">
                  {shownPassed.map((item) => (
                    <ToplistCard key={item.id} item={item} disabled={full} onClick={() => onAdd(item.id)} />
                  ))}
                </div>
                {shownPassed.length < passed.length ? (
                  <Button variant="chip" size="sm" className="mt-3" onClick={() => setShowAllPassed(true)}>
                    <FormattedMessage
                      id="grow.games.toplist.result.showAll"
                      defaultMessage="Show all {count}"
                      values={{ count: passed.length }}
                    />
                  </Button>
                ) : null}
              </div>
            ) : null}
          </ToplistSearch>
        </div>
      )}

      <ConfirmDialog
        open={confirmDiscard}
        title={<FormattedMessage id="grow.games.toplist.result.discardTitle" defaultMessage="Discard your ranking?" />}
        message={
          <FormattedMessage
            id="grow.games.toplist.result.discardMessage"
            defaultMessage="Your top {count} will not be saved to your profile."
            values={{ count: keep }}
          />
        }
        confirmLabel={<FormattedMessage id="common.discard" defaultMessage="Discard" />}
        onConfirm={() => {
          setConfirmDiscard(false);
          onRestart();
        }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </fieldset>
  );
}
